import { createRemoteComponent } from '@module-federation/bridge-react';
import {
  loadRemote,
  registerRemotes,
} from '@module-federation/enhanced/runtime';
import { twAuthClient } from '@teamworksdev/web-sdk/auth';
import { getRemote, Remote } from '@teamworksdev/web-sdk/module-federation';
import { ComponentType, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { environment } from '../../../environment';
import RemoteAppContainerError from '../../components/error/RemoteAppContainerError';
import { MFELoading } from '../../components/loading/MFELoading';

const loadAmsDashboard = async ({ force }: { force?: boolean } = {}) => {
  registerRemotes(
    [getRemote(
      environment.VITE_TW_MFE_ENV ?? (environment.VITE_ENVIRONMENT.startsWith('production') ? 'production' : 'staging'),
      environment.VITE_REGION,
      'ams-dashboard',
    ) as Remote],
    { force },
  );

  return loadRemote('ams_dashboard/ams_dashboard');
};

type AmsDashboardMFEProps = {
  category: string;
  locale: string;
};

const AmsDashboardFallback = () => {
  const { t } = useTranslation();
  return (
    <RemoteAppContainerError
      refresh={() => loadAmsDashboard({ force: true }).then(() => window.location.reload())}
      failureHeader={t('amsDashboardLoadFailureHeader')}
      failureDescription={t('amsDashboardLoadFailureBody')}
    />
  );
};

const AmsDashboardRemote = createRemoteComponent({
  loader: () => loadAmsDashboard(),
  fallback: AmsDashboardFallback,
  loading: <MFELoading />,
}) as unknown as ComponentType<AmsDashboardMFEProps & { style?: React.CSSProperties }>;

// twAuthClient.init() is async (makes an HTTP call to /unity-w-token). The MFE
// can finish loading from CDN before init completes, causing "No access token
// available". Poll until initialized, then render the remote.
const AUTH_INIT_TIMEOUT_MS = 10_000;

export const AmsDashboardMFE = ({ category, locale }: AmsDashboardMFEProps) => {
  const [authReady, setAuthReady] = useState(() => twAuthClient.getSnapshot().initialized);
  const [authTimedOut, setAuthTimedOut] = useState(false);

  useEffect(() => {
    if (authReady || authTimedOut) return;

    const interval = setInterval(() => {
      if (twAuthClient.getSnapshot().initialized) {
        setAuthReady(true);
        clearTimeout(timeout);
        clearInterval(interval);
      }
    }, 100);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setAuthTimedOut(true);
    }, AUTH_INIT_TIMEOUT_MS);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [authReady, authTimedOut]);

  if (authTimedOut && !authReady) {
    return <AmsDashboardFallback />;
  }

  if (!authReady) {
    return <MFELoading />;
  }

  return (
    <AmsDashboardRemote
      category={category}
      locale={locale}
      style={{ display: 'flex', flex: 1, width: '100%', height: '100%' }}
    />
  );
};
